import React, { createContext, useState, useContext, useCallback, useRef, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../config';

const BackgroundJobsContext = createContext();

const STORAGE_KEY = 'facelink_bg_jobs';
const NOTIFICATION_HISTORY_KEY = 'facelink_notification_history';
const MAX_NOTIFICATION_HISTORY = 100;
const POLL_INTERVAL_MS = 2500;
const MAX_POLL_ATTEMPTS = 2400; // ~100 min for long runs (e.g. 10k photos)

function loadJobsFromStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { encoding: [], deletion: [] };
    const data = JSON.parse(raw);
    const encoding = Array.isArray(data.encoding) ? data.encoding : [];
    const deletion = Array.isArray(data.deletion) ? data.deletion : [];
    return { encoding, deletion };
  } catch {
    return { encoding: [], deletion: [] };
  }
}

function saveJobsToStorage(encoding, deletion) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ encoding, deletion }));
  } catch {}
}

function loadNotificationHistory() {
  try {
    const raw = localStorage.getItem(NOTIFICATION_HISTORY_KEY);
    if (!raw) return [];
    const list = JSON.parse(raw);
    return Array.isArray(list) ? list : [];
  } catch {
    return [];
  }
}

function saveNotificationHistory(list) {
  try {
    const trimmed = list.slice(0, MAX_NOTIFICATION_HISTORY);
    localStorage.setItem(NOTIFICATION_HISTORY_KEY, JSON.stringify(trimmed));
  } catch {}
}

export const BackgroundJobsProvider = ({ children }) => {
  const [encodingJobs, setEncodingJobs] = useState(() => loadJobsFromStorage().encoding);
  const [deletionJobs, setDeletionJobs] = useState(() => loadJobsFromStorage().deletion);
  const [completions, setCompletions] = useState([]);
  const [notificationHistory, setNotificationHistory] = useState(() => loadNotificationHistory());
  const pollAttemptsRef = useRef({ encoding: {}, deletion: {} });
  const completionIdRef = useRef(0);

  const addToNotificationHistory = useCallback((item) => {
    setNotificationHistory((prev) => {
      const next = [{ ...item, createdAt: item.createdAt || new Date().toISOString() }, ...prev];
      saveNotificationHistory(next);
      return next;
    });
  }, []);

  const addCompletion = useCallback((item) => {
    const id = item.id || `comp-${++completionIdRef.current}`;
    setCompletions((prev) => [...prev, { ...item, id }]);
  }, []);

  const addEncodingJob = useCallback(({ batchId, total, label, eventName }) => {
    if (!batchId || total == null) return;
    setEncodingJobs((prev) => {
      if (prev.some((j) => j.batchId === batchId)) return prev;
      return [...prev, { id: `enc-${batchId}`, batchId, total, current: 0, label: label || `Batch #${batchId}`, eventName: eventName || null }];
    });
  }, []);

  const updateEncodingProgress = useCallback((batchId, current) => {
    setEncodingJobs((prev) =>
      prev.map((j) => (j.batchId === batchId ? { ...j, current } : j))
    );
  }, []);

  const removeEncodingJob = useCallback((batchId, options = {}) => {
    const { notify = false, label, eventName } = options;
    if (notify && label) {
      const id = `enc-${batchId}-${++completionIdRef.current}`;
      const item = { id, type: 'encoding', label, eventName: eventName || null };
      setCompletions((prev) => [...prev, item]);
      addToNotificationHistory(item);
    }
    setEncodingJobs((prev) => prev.filter((j) => j.batchId !== batchId));
    pollAttemptsRef.current.encoding[batchId] = undefined;
  }, [addToNotificationHistory]);

  const removeDeletionJob = useCallback((batchId, options = {}) => {
    const { notify = false, label, eventName } = options;
    if (notify && label) {
      const id = `del-${batchId}-${++completionIdRef.current}`;
      const item = { id, type: 'deletion', label, eventName: eventName || null };
      setCompletions((prev) => [...prev, item]);
      addToNotificationHistory(item);
    }
    setDeletionJobs((prev) => prev.filter((j) => j.batchId !== batchId));
    pollAttemptsRef.current.deletion[batchId] = undefined;
  }, [addToNotificationHistory]);

  const dismissCompletion = useCallback((id) => {
    setCompletions((prev) => prev.filter((c) => c.id !== id));
  }, []);

  const clearNotificationHistory = useCallback(() => {
    setNotificationHistory([]);
    saveNotificationHistory([]);
  }, []);

  const addDeletionJob = useCallback(({ batchId, total, label, eventName }) => {
    if (!batchId) return;
    setDeletionJobs((prev) => {
      if (prev.some((j) => j.batchId === batchId)) return prev;
      const count = total != null ? total : 0;
      return [...prev, { id: `del-${batchId}`, batchId, total: count, label: label || `Batch #${batchId}`, eventName: eventName || null }];
    });
  }, []);

  // Persist jobs to localStorage so they survive logout, login, and page reload
  useEffect(() => {
    saveJobsToStorage(encodingJobs, deletionJobs);
  }, [encodingJobs, deletionJobs]);

  // Poll encoding jobs: GET /batches/:id, update current from encoded count; remove when current === total
  useEffect(() => {
    if (encodingJobs.length === 0) return;
    const interval = setInterval(async () => {
      for (const job of encodingJobs) {
        const attempts = (pollAttemptsRef.current.encoding[job.batchId] || 0) + 1;
        pollAttemptsRef.current.encoding[job.batchId] = attempts;
        if (attempts > MAX_POLL_ATTEMPTS) {
          removeEncodingJob(job.batchId);
          continue;
        }
        try {
          const res = await axios.get(`${API_URL}/batches/${job.batchId}`);
          const batch = res.data;
          const approved = (batch.photos || []).filter((p) => p.status === 'approved');
          const encoded = approved.filter((p) => p.is_encoded).length;
          const total = approved.length;
          updateEncodingProgress(job.batchId, encoded);
          if (total > 0 && encoded >= total) {
            removeEncodingJob(job.batchId, { notify: true, label: job.label, eventName: job.eventName });
          }
        } catch (err) {
          if (err.response?.status === 404) removeEncodingJob(job.batchId);
        }
      }
    }, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [encodingJobs, updateEncodingProgress, removeEncodingJob]);

  // Poll deletion jobs: GET /batches (or /events/batches for user), if batch not in list, remove job
  useEffect(() => {
    if (deletionJobs.length === 0) return;
    const interval = setInterval(async () => {
      for (const job of deletionJobs) {
        const attempts = (pollAttemptsRef.current.deletion[job.batchId] || 0) + 1;
        pollAttemptsRef.current.deletion[job.batchId] = attempts;
        if (attempts > MAX_POLL_ATTEMPTS) {
          removeDeletionJob(job.batchId);
          continue;
        }
        try {
          const adminRes = await axios.get(`${API_URL}/batches`).catch(() => ({ data: [] }));
          const userRes = await axios.get(`${API_URL}/events/batches`).catch(() => ({ data: [] }));
          const adminIds = (adminRes.data || []).map((b) => b.id);
          const userIds = (userRes.data || []).map((b) => b.id);
          const allIds = [...new Set([...adminIds, ...userIds])];
          if (!allIds.includes(job.batchId)) {
            removeDeletionJob(job.batchId, { notify: true, label: job.label, eventName: job.eventName });
          }
        } catch {
          // keep job and retry
        }
      }
    }, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [deletionJobs, removeDeletionJob]);

  const value = {
    encodingJobs,
    deletionJobs,
    completions,
    notificationHistory,
    clearNotificationHistory,
    addToNotificationHistory,
    addCompletion,
    addEncodingJob,
    addDeletionJob,
    dismissCompletion,
  };

  return (
    <BackgroundJobsContext.Provider value={value}>
      {children}
    </BackgroundJobsContext.Provider>
  );
};

export const useBackgroundJobs = () => {
  const ctx = useContext(BackgroundJobsContext);
  if (!ctx) throw new Error('useBackgroundJobs must be used within BackgroundJobsProvider');
  return ctx;
};
